import { useEffect, useMemo, useState } from "react";
import UpdateJobStages from "@components/dashboard/UpdateJobStages";
import { useProjectActions } from "@contexts/projects";
import {
  createProjectTitleAnnulments,
  previewProjectTitleAnnulments,
  revokeProjectTitleAnnulment,
} from "@contexts/projects/titleAnnulmentsApi";
import { brl, titleCount, totalsOf, PENDING_BANNER_TEXT } from "./summary";

// Gestão da lista "Títulos anulados na Ampli" (design D6), usada pela página "Títulos > Títulos
// anulados" (`variant="page"`) e pela barra lateral (`variant="offcanvas"`). A construtora e a
// empresa no ERP vêm da obra aberta: o cliente só cola os números. Na página a lista é da
// construtora (valores do título em todas as obras, obras atingidas e situação); na barra
// lateral os valores são a parte da obra aberta. Estilos em `layout/_title-annulments.scss`:
// o reset de `theme/_fonts.scss` zera tamanho e peso de toda tag, então cada texto tem classe.

export const REASON_MIN = 10;
export const REASON_MAX = 500;
export const MAX_NUMBERS = 50;

export const CATEGORY_LABELS = {
  erp_forecast: "Previsão do ERP (PRV)",
  duplicate: "Duplicado",
  out_of_scope: "Fora do escopo da análise",
  other: "Outro motivo",
};

export const STATUS_LABELS = {
  found: "Encontrado",
  not_found: "Não encontrado na última medição",
  already_annulled: "Já anulado",
  ambiguous: "Ambíguo: escolha a empresa",
};

const STATUS_TONES = { found: "success", not_found: "error", already_annulled: "neutral", ambiguous: "warning" };

const SKIPPED_LABELS = {
  already_annulled: "já anulado",
  not_found: "não encontrado na última medição",
  ambiguous: "empresa não escolhida",
  invalid_bill_id: "número inválido",
  too_many_titles: "acima do limite de 50 por vez",
};

const ORIGIN_LABELS = { ampli_obras: "Ampli Obras", backoffice: "Backoffice", director: "Diretoria" };

const isMega = (erp) => String(erp || "").toUpperCase() === "MEGA";
const erpLabel = (erp) => (isMega(erp) ? "MEGA" : "Sienge");

const civil = (value) => {
  if (typeof value !== "string" || value.length < 10) return "—";
  const [y, m, d] = value.slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "—";
};

export const parseNumbers = (text) => [...new Set(
  String(text || "").split(/[\s,;]+/).map((value) => value.trim()).filter(Boolean),
)];

const validReason = (value) => {
  const { length } = String(value || "").trim();
  return length >= REASON_MIN && length <= REASON_MAX;
};

const buildingName = (building) => (
  typeof building === "string" ? building : building?.buildingName ?? building?.name ?? building?.buildingId ?? ""
);

const buildingList = (title) => (Array.isArray(title?.buildings) ? title.buildings : [])
  .map(buildingName).filter(Boolean).join(", ");

// Hora do desbloqueio: "14h" (ou "14h30"), no fuso de Brasília.
export const formatRetryTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(date).map(({ type, value: part }) => [type, part]),
  );
  return parts.minute === "00" ? `${Number(parts.hour)}h` : `${Number(parts.hour)}h${parts.minute}`;
};

// `update` = { queued | blocked | alreadyRunning | skipped } (D2). Aceita tanto o formato de
// chaves quanto `{ status }`.
export const updateOutcome = (update) => {
  if (!update || typeof update !== "object") return { kind: "none" };
  const kinds = ["queued", "blocked", "alreadyRunning", "skipped"];
  const kind = typeof update.status === "string" && kinds.includes(update.status)
    ? update.status
    : kinds.find((key) => update[key]);
  if (!kind) return { kind: "none" };
  const detail = typeof update[kind] === "object" && update[kind] !== null ? update[kind] : {};
  return {
    kind,
    job: update.job ?? detail.job ?? null,
    retryAfter: update.retryAfter ?? detail.retryAfter ?? null,
  };
};

const updateMessage = ({ kind, retryAfter }) => {
  switch (kind) {
    case "queued":
      return null;
    case "blocked": {
      const time = formatRetryTime(retryAfter);
      return `A obra continua com a atualização do financeiro pendente. ${time ? `O financeiro poderá ser atualizado a partir das ${time}.` : "Tente atualizar o financeiro mais tarde."}`;
    }
    case "alreadyRunning":
      return "Já há uma atualização em andamento nesta construtora. A obra continua com a atualização do financeiro pendente: atualize o financeiro quando ela terminar.";
    case "skipped":
      return "A atualização do financeiro não foi disparada. A obra continua com a atualização do financeiro pendente.";
    default:
      return null;
  }
};

const errorText = (result) => (
  result?.body?.message || (result?.status === 403
    ? "Você não tem permissão para alterar a lista."
    : "Não foi possível salvar. Tente de novo.")
);

const candidateLabel = (candidate) => (
  `${candidate.legalEntityName || `Empresa ${candidate.legalEntityId}`}${candidate.creditorName ? ` · ${candidate.creditorName}` : ""}${candidate.billId != null ? ` · ${candidate.billId}` : ""}`
);

// Barra lateral: `thisBuilding` é a parte que saiu da obra aberta (título rateado).
const buildingTotals = (item) => {
  const src = item.thisBuilding ?? item;
  const toPay = Number(src.removedToPay ?? item.effect?.removedToPay) || 0;
  const paid = Number(src.removedPaid ?? item.effect?.removedPaid) || 0;
  const total = src.removedTotal != null && Number.isFinite(Number(src.removedTotal)) ? Number(src.removedTotal) : toPay + paid;
  return { toPay, paid, total };
};

// Página: o título inteiro, somando a parte de cada obra no último processamento.
const companyTotals = (item) => {
  const parts = Array.isArray(item.effect?.buildings) ? item.effect.buildings : [];
  const toPay = parts.reduce((sum, part) => sum + (Number(part.removedToPay) || 0), 0);
  const paid = parts.reduce((sum, part) => sum + (Number(part.removedPaid) || 0), 0);
  return { toPay, paid, total: toPay + paid };
};

// Situação do título na lista: aplicado, aguardando a atualização ou não achado no último run.
const itemSituation = (item) => {
  if (item.notFoundInLastRun) return { tone: "error", label: "Não encontrado no último processamento" };
  if (item.pendingApplication || !item.effect) return { tone: "warning", label: "Aguardando atualização" };
  return { tone: "success", label: "Aplicado" };
};

const numberCell = (item, mega) => {
  const shown = item.titleNumber ?? item.billId ?? "—";
  return mega && item.billId != null ? `${shown} · AP ${item.billId}` : String(shown);
};

const personOf = (value) => value?.name || "Usuário";

const Badge = ({ tone, children }) => <span className={`ta-badge ta-badge--${tone}`}>{children}</span>;

const Kpi = ({ label, value, tone }) => (
  <div className={`ta-kpi${tone ? ` ta-kpi--${tone}` : ""}`}>
    <p className="ta-kpi__label">{label}</p>
    <p className="ta-kpi__value">{value}</p>
  </div>
);

// `projects` (lista de obras do usuário) só serve para dar nome às obras atingidas na página.
const TitleAnnulmentsManager = ({ variant = "offcanvas", projectId, status, data, project, projects, erp: erpProp, reload }) => {
  const { getUpdateJob, selectProject } = useProjectActions() ?? {};
  const page = variant === "page";
  const [numbers, setNumbers] = useState("");
  const [legalEntityId, setLegalEntityId] = useState("");
  const [changingEntity, setChangingEntity] = useState(false);
  const [preview, setPreview] = useState(null);
  const [included, setIncluded] = useState({});
  const [chosen, setChosen] = useState({});
  // Prévia refeita para a empresa escolhida de cada linha ambígua: { [índice]: { value, title } }.
  const [resolved, setResolved] = useState({});
  const [category, setCategory] = useState("erp_forecast");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [stages, setStages] = useState(null);

  // Outra obra: formulário e andamento começam do zero.
  useEffect(() => {
    setNumbers("");
    setPreview(null);
    setIncluded({});
    setChosen({});
    setResolved({});
    setReason("");
    setMessage(null);
    setRevoking(null);
    setStages(null);
    setLegalEntityId("");
    setChangingEntity(false);
  }, [projectId]);

  const canWrite = data?.canWrite === true;
  const items = Array.isArray(data?.items) ? data.items : [];
  const revoked = Array.isArray(data?.revoked) ? data.revoked : [];
  const legalEntities = Array.isArray(data?.legalEntities) ? data.legalEntities : [];
  const erp = data?.erp ?? erpProp ?? items[0]?.erp ?? project?.erp;
  const mega = isMega(erp);
  const entityLabel = mega ? "Filial no MEGA" : "Empresa no Sienge";
  const entityValue = legalEntityId
    || (data?.defaultLegalEntityId != null ? String(data.defaultLegalEntityId) : "")
    || (legalEntities.length === 1 ? String(legalEntities[0].id) : "");
  const entityName = legalEntities.find((entity) => String(entity.id) === entityValue)?.name ?? entityValue;
  // Empresa já resolvida pela obra: mostra o nome, sem pedir escolha. Só pede quando não há
  // padrão ou quando o usuário quer trocar (construtora com mais de uma SPE).
  const entityFixed = !changingEntity
    && (data?.defaultLegalEntityId != null || legalEntities.length === 1)
    && entityValue !== "";
  const refreshAfterJob = () => {
    reload?.();
    if (projectId) selectProject?.(projectId);
  };

  const names = useMemo(() => new Map((Array.isArray(projects) ? projects : [])
    .map((entry) => [String(entry?.building_id ?? ""), entry?.building_name ?? entry?.name ?? null])
    .filter(([id, name]) => id && name)), [projects]);
  const buildingsOf = (item) => {
    const ids = [...new Set([
      ...(Array.isArray(item.buildingIds) ? item.buildingIds : []),
      ...(Array.isArray(item.effect?.buildings) ? item.effect.buildings.map((part) => part.buildingId) : []),
    ].map(String))];
    return ids.map((id) => names.get(id) ?? id);
  };

  const summary = data?.summary ?? project?.titleAnnulments ?? null;
  const obraTotals = useMemo(() => totalsOf(summary), [summary]);
  const totals = useMemo(() => {
    if (!page) return obraTotals;
    const sums = items.map(companyTotals);
    const toPay = sums.reduce((sum, entry) => sum + entry.toPay, 0);
    const paid = sums.reduce((sum, entry) => sum + entry.paid, 0);
    return { count: items.length, toPay, paid, total: toPay + paid };
  }, [page, items, obraTotals]);
  const parsed = parseNumbers(numbers);
  const tooMany = parsed.length > MAX_NUMBERS;
  // `pending: null` explícito na lista carregada vale: a lista é mais nova que o resumo da obra.
  const pending = data && Object.prototype.hasOwnProperty.call(data, "pending")
    ? data.pending
    : project?.titleAnnulments?.pending ?? null;

  // Candidato escolhido + título da prévia refeita para a empresa (traz billId e valores). A
  // prévia ambígua não traz billId; sem ela, no Sienge o número digitado é o billId.
  const ambiguousTitle = (row, index, picked) => {
    if (!picked) return null;
    const refined = resolved[index]?.value === String(chosen[index]) ? resolved[index].title : null;
    const typed = Number(row.input);
    const billId = refined?.billId ?? picked.billId ?? (!mega && Number.isFinite(typed) ? typed : undefined);
    if (billId === undefined) return null;
    return { ...picked, ...refined, legalEntityId: refined?.legalEntityId ?? picked.legalEntityId, billId };
  };

  const pickCandidate = async (row, index, value) => {
    setChosen((current) => ({ ...current, [index]: value }));
    if (value === "") return;
    const candidate = (Array.isArray(row.candidates) ? row.candidates : [])[Number(value)];
    if (!candidate || candidate.legalEntityId == null) return;
    const result = await previewProjectTitleAnnulments(projectId, {
      numbers: [row.input], legalEntityId: Number(candidate.legalEntityId),
    });
    const found = result?.ok ? (result.body?.results ?? []).find((entry) => entry?.status === "found" && entry.title) : null;
    if (found) setResolved((current) => ({ ...current, [index]: { value, title: found.title } }));
  };

  const selectable = (row, index) => {
    if (!row || included[index] === false) return null;
    if (row.status === "found") return row.title ?? null;
    if (row.status === "ambiguous") {
      const candidates = Array.isArray(row.candidates) ? row.candidates : [];
      return ambiguousTitle(row, index, candidates[Number(chosen[index])]);
    }
    return null;
  };
  const results = preview?.results ?? [];
  const toSend = results.map(selectable).filter(Boolean);
  const reasonOk = validReason(reason);
  const canConfirm = canWrite && !busy && toSend.length > 0 && reasonOk;

  const lookup = async (event) => {
    event.preventDefault();
    if (!parsed.length || tooMany || busy) return;
    setBusy(true);
    setMessage(null);
    const body = { numbers: parsed };
    if (entityValue !== "" && Number.isFinite(Number(entityValue))) body.legalEntityId = Number(entityValue);
    const result = await previewProjectTitleAnnulments(projectId, body);
    setBusy(false);
    if (result?.ok) {
      setPreview(result.body || { results: [] });
      setIncluded({});
      setChosen({});
      setResolved({});
    } else {
      setPreview(null);
      setMessage({ tone: "error", text: errorText(result) });
    }
  };

  const finish = (body, successText) => {
    const outcome = updateOutcome(body?.update);
    const skipped = Array.isArray(body?.skipped) ? body.skipped : [];
    const skippedText = skipped.length
      ? ` Ficaram de fora: ${skipped.map((entry) => `${entry.billId} (${SKIPPED_LABELS[entry.reason] || entry.reason})`).join(", ")}.`
      : "";
    const extra = updateMessage(outcome);
    setMessage({ tone: "success", text: `${successText}${skippedText}${extra ? ` ${extra}` : ""}` });
    setStages(outcome.kind === "queued" ? { job: outcome.job } : null);
    reload?.();
  };

  const clearForm = () => {
    setPreview(null);
    setNumbers("");
    setReason("");
    setIncluded({});
    setChosen({});
    setResolved({});
  };

  const confirm = async () => {
    if (!canConfirm) return;
    setBusy(true);
    setMessage(null);
    const result = await createProjectTitleAnnulments(projectId, {
      items: toSend.map((title) => ({
        legalEntityId: title.legalEntityId ?? (entityValue !== "" ? Number(entityValue) : undefined),
        billId: title.billId,
      })),
      category,
      reason: reason.trim(),
      applyNow: true,
    });
    setBusy(false);
    if (!result?.ok) {
      setMessage({ tone: "error", text: errorText(result) });
      return;
    }
    const created = Array.isArray(result.body?.created) ? result.body.created.length : toSend.length;
    finish(result.body, `${titleCount(created)} ${created === 1 ? "anulado" : "anulados"} na Ampli.`);
    clearForm();
  };

  const revoke = async (event) => {
    event.preventDefault();
    if (!revoking || !validReason(revoking.reason) || busy) return;
    setBusy(true);
    setMessage(null);
    const result = await revokeProjectTitleAnnulment(projectId, revoking.item.id, {
      revokeReason: revoking.reason.trim(),
      applyNow: true,
    });
    setBusy(false);
    if (!result?.ok) {
      setMessage({ tone: "error", text: errorText(result) });
      return;
    }
    finish(result.body, `Título ${revoking.item.titleNumber ?? revoking.item.billId} reincluído.`);
    setRevoking(null);
  };

  const numberHeader = `Nº ${erpLabel(erp)}`;
  const columns = 7 + (canWrite ? 1 : 0);

  return (
    <div className={`ta-manager ta-manager--${variant}`}>
      <div
        className="ta-kpis"
        data-testid="annulments-summary"
        aria-label={`${titleCount(totals.count)} · A pagar ${brl(totals.toPay)} · Pago ${brl(totals.paid)} · Total ${brl(totals.total)}`}
      >
        <Kpi label={page ? "Títulos anulados" : "Títulos nesta obra"} value={titleCount(totals.count)} />
        <Kpi label="Fora de a pagar" value={brl(totals.toPay)} tone="topay" />
        <Kpi label="Fora de pago" value={brl(totals.paid)} tone="paid" />
        <Kpi label="Total fora da conta" value={brl(totals.total)} />
      </div>

      {status === "loading" && !data && <p className="ta-muted">Carregando…</p>}
      {status === "error" && !data && (
        <p className="ta-muted">Não foi possível carregar os títulos anulados. Tente novamente.</p>
      )}

      {pending && (
        <div className="ta-callout ta-callout--warning" role="note">
          <p className="ta-callout__title">Atualização do financeiro pendente</p>
          <p className="ta-callout__text">{`${PENDING_BANNER_TEXT}. Os valores só refletem a lista depois do próximo processamento financeiro.`}</p>
        </div>
      )}

      {message && (
        <div className={`ta-callout ta-callout--${message.tone === "error" ? "error" : "success"}`} role="status" aria-live="polite">
          <p className="ta-callout__text">{message.text}</p>
        </div>
      )}

      {stages && (
        <UpdateJobStages
          initialJob={stages.job}
          fetchJob={async () => {
            const res = await getUpdateJob?.(project?.building_id);
            return { ok: !!res?.ok, job: res?.body?.job ?? null };
          }}
          onTerminal={(job) => {
            // Sem recarregar a página: um segundo lote em preenchimento não se perde.
            if (job?.status === "succeeded") refreshAfterJob();
          }}
        />
      )}

      {data && !canWrite && data.selfService === false && (
        <p className="ta-muted" role="note">
          A anulação de títulos pelo Ampli Obras não está habilitada para esta construtora. A lista aparece só para consulta.
        </p>
      )}

      {canWrite && (
        <section className="ta-panel" aria-label="Anular títulos">
          <header className="ta-panel__header">
            <h3 className="ta-panel__title">Anular títulos</h3>
            <p className="ta-panel__hint">
              Cole os números do ERP, confira a prévia e confirme. O título sai inteiro, a pagar e pago, em todas as obras da construtora.
            </p>
          </header>
          <form className="ta-form" onSubmit={lookup}>
            <div className="ta-field ta-field--wide">
              <label className="ta-field__label" htmlFor={`annulments-numbers-${variant}`}>
                {`Números dos títulos (${mega ? "AP ou documento" : "Sienge"})`}
              </label>
              <textarea
                id={`annulments-numbers-${variant}`}
                className="ta-input ta-input--area"
                rows={3}
                placeholder={mega ? "Ex.: 45120, 8700/1" : "Ex.: 8834, 20146"}
                value={numbers}
                aria-describedby={`annulments-numbers-hint-${variant}`}
                onChange={(event) => { setNumbers(event.target.value); setPreview(null); }}
              />
              <p className="ta-field__hint" id={`annulments-numbers-hint-${variant}`}>
                {`Separe por vírgula, espaço ou quebra de linha. Até ${MAX_NUMBERS} por vez.`}
              </p>
              {tooMany && (
                <p role="alert" className="ta-field__error">{`O limite é de ${MAX_NUMBERS} números por vez.`}</p>
              )}
            </div>
            <div className="ta-form__row">
              {entityFixed ? (
                <div className="ta-field">
                  <p className="ta-field__label">{entityLabel}</p>
                  <p className="ta-field__static">
                    {entityName}
                    {legalEntities.length > 1 && (
                      <button type="button" className="ta-link" onClick={() => setChangingEntity(true)}>Trocar</button>
                    )}
                  </p>
                </div>
              ) : (
                <div className="ta-field">
                  <label className="ta-field__label" htmlFor={`annulments-entity-${variant}`}>{entityLabel}</label>
                  {legalEntities.length > 0 ? (
                    <select
                      id={`annulments-entity-${variant}`}
                      className="ta-input"
                      value={entityValue}
                      onChange={(event) => { setLegalEntityId(event.target.value); setPreview(null); }}
                    >
                      {entityValue === "" && <option value="">Escolha a empresa</option>}
                      {legalEntities.map((entity) => (
                        <option key={entity.id} value={String(entity.id)}>{entity.name ?? entity.id}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={`annulments-entity-${variant}`}
                      className="ta-input"
                      inputMode="numeric"
                      value={entityValue}
                      onChange={(event) => { setLegalEntityId(event.target.value); setPreview(null); }}
                    />
                  )}
                </div>
              )}
              <button
                type="submit"
                className="button button-accent ta-form__submit"
                disabled={busy || parsed.length === 0 || tooMany}
              >
                Ver títulos
              </button>
            </div>
          </form>

          {preview && (
            <div className="ta-preview">
              <div className="ta-table-wrap">
                <table className="ta-table" aria-label="Prévia dos títulos">
                  <thead>
                    <tr>
                      <th className="ta-table__check"><span className="ta-sr-only">Incluir</span></th>
                      <th>Número</th>
                      <th>Situação</th>
                      <th>Credor</th>
                      <th>Documento</th>
                      <th className="ta-num">A pagar</th>
                      <th className="ta-num">Pago</th>
                      <th className="ta-num">Total</th>
                      <th>Obras</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((row, index) => {
                      const ambiguous = row.status === "ambiguous";
                      const candidates = Array.isArray(row.candidates) ? row.candidates : [];
                      const title = row.status === "found"
                        ? row.title ?? null
                        : ambiguous ? ambiguousTitle(row, index, candidates[Number(chosen[index])]) : null;
                      // "Já anulado" também mostra o título (esmaecido), para o usuário reconhecer.
                      const shown = title ?? (row.status === "already_annulled" ? row.title : null) ?? {};
                      const known = shown.toPay != null || shown.paid != null;
                      const toPay = Number(shown.toPay) || 0;
                      const paid = Number(shown.paid) || 0;
                      const muted = !(row.status === "found" || (ambiguous && title));
                      return (
                        <tr key={`${row.input}-${index}`} className={muted ? "ta-table__row--muted" : undefined}>
                          <td className="ta-table__check">
                            {(row.status === "found" || (ambiguous && chosen[index] !== undefined && chosen[index] !== "")) && (
                              <input
                                type="checkbox"
                                aria-label={`Incluir ${row.input}`}
                                checked={included[index] !== false}
                                onChange={(event) => setIncluded((current) => ({ ...current, [index]: event.target.checked }))}
                              />
                            )}
                          </td>
                          <td className="ta-strong">{row.input}</td>
                          <td>
                            <Badge tone={STATUS_TONES[row.status] ?? "neutral"}>{STATUS_LABELS[row.status] || row.status}</Badge>
                            {ambiguous && (
                              <select
                                className="ta-input ta-input--compact"
                                aria-label={`Empresa de ${row.input}`}
                                value={chosen[index] ?? ""}
                                onChange={(event) => pickCandidate(row, index, event.target.value)}
                              >
                                <option value="">Escolha</option>
                                {candidates.map((candidate, candidateIndex) => (
                                  <option key={`${candidate.legalEntityId}-${candidate.billId}-${candidateIndex}`} value={String(candidateIndex)}>
                                    {candidateLabel(candidate)}
                                  </option>
                                ))}
                              </select>
                            )}
                            {(Array.isArray(shown.warnings) ? shown.warnings : []).map((warning) => (
                              <p key={warning} className="ta-cell-note" role="note">{warning}</p>
                            ))}
                          </td>
                          <td>{shown.creditorName || "—"}</td>
                          <td>{shown.documentType || "—"}</td>
                          <td className="ta-num">{known ? brl(toPay) : "—"}</td>
                          <td className="ta-num">{known ? brl(paid) : "—"}</td>
                          <td className="ta-num ta-strong">{known ? brl(shown.total ?? toPay + paid) : "—"}</td>
                          <td>{buildingList(shown) || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="ta-form ta-form--confirm">
                <div className="ta-form__row">
                  <div className="ta-field">
                    <label className="ta-field__label" htmlFor={`annulments-category-${variant}`}>Categoria</label>
                    <select
                      id={`annulments-category-${variant}`}
                      className="ta-input"
                      value={category}
                      onChange={(event) => setCategory(event.target.value)}
                    >
                      {Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="ta-field ta-field--wide">
                  <label className="ta-field__label" htmlFor={`annulments-reason-${variant}`}>Motivo</label>
                  <textarea
                    id={`annulments-reason-${variant}`}
                    className="ta-input ta-input--area"
                    rows={2}
                    maxLength={REASON_MAX}
                    placeholder="Ex.: PRV que duplica a previsão da obra"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                  <p className="ta-field__hint">{`Entre ${REASON_MIN} e ${REASON_MAX} caracteres · ${reason.trim().length}/${REASON_MAX}`}</p>
                  {reason.trim().length > 0 && !reasonOk && (
                    <p role="alert" className="ta-field__error">{`Informe um motivo de no mínimo ${REASON_MIN} caracteres.`}</p>
                  )}
                </div>
                <p className="ta-muted">Vale também para a Diretoria. O ERP não muda; os números só mudam quando o financeiro é atualizado.</p>
                <div className="ta-actions">
                  <button type="button" className="button button-accent" disabled={!canConfirm} onClick={confirm}>
                    Anular e atualizar financeiro
                  </button>
                  <button type="button" className="button button-ghost" disabled={busy} onClick={clearForm}>
                    Limpar
                  </button>
                  {toSend.length > 0 && <p className="ta-muted">{`${titleCount(toSend.length)} selecionado${toSend.length === 1 ? "" : "s"}`}</p>}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      <section className="ta-panel" aria-label="Títulos anulados">
        <header className="ta-panel__header">
          <h3 className="ta-panel__title">{`Títulos anulados (${items.length})`}</h3>
          <p className="ta-panel__hint">
            {page
              ? "Lista da construtora: vale para todas as obras, no Ampli Obras e na Diretoria. Valores do último processamento."
              : "Valores que saíram desta obra no último processamento."}
          </p>
        </header>
        <div className="ta-table-wrap">
          <table className="ta-table">
            <thead>
              <tr>
                <th>{numberHeader}</th>
                <th>Credor</th>
                <th className="ta-num">A pagar</th>
                <th className="ta-num">Pago</th>
                <th className="ta-num">Total</th>
                <th>Motivo</th>
                <th>Anulado por</th>
                {canWrite && <th><span className="ta-sr-only">Ações</span></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const t = page ? companyTotals(item) : buildingTotals(item);
                // Barra lateral: título que não atinge esta obra (thisBuilding nulo) fica sem valores daqui.
                const other = !page && item.affectsThisBuilding === false;
                // Página: sem processamento depois da anulação ainda não há valor apurado.
                const unknown = other || (page && !item.effect);
                const situation = itemSituation(item);
                const isRevoking = revoking?.item?.id === item.id;
                return [
                  <tr key={item.id} className={isRevoking ? "ta-table__row--active" : undefined}>
                    <td>
                      <p className="ta-strong ta-tabular">{numberCell(item, mega)}</p>
                      <p className="ta-cell-sub">{item.documentType || "—"}</p>
                      {page && <Badge tone={situation.tone}>{situation.label}</Badge>}
                    </td>
                    <td>
                      <p className="ta-cell-text">{item.creditorName || "—"}</p>
                      {other && <p className="ta-cell-note" role="note">Atinge outras obras</p>}
                      {page && <p className="ta-cell-sub">{`Obras: ${buildingsOf(item).join(", ") || "—"}`}</p>}
                    </td>
                    <td className="ta-num">{unknown ? "—" : brl(t.toPay)}</td>
                    <td className="ta-num">{unknown ? "—" : brl(t.paid)}</td>
                    <td className="ta-num ta-strong">{unknown ? "—" : brl(t.total)}</td>
                    <td className="ta-table__reason">
                      <Badge tone="accent">{CATEGORY_LABELS[item.category] || item.category || "—"}</Badge>
                      {item.reason ? <p className="ta-cell-text">{item.reason}</p> : null}
                    </td>
                    <td>
                      <p className="ta-cell-text">{`${personOf(item.createdBy)}, ${civil(item.createdAt)}`}</p>
                      {ORIGIN_LABELS[item.createdVia] ? <p className="ta-cell-sub">{ORIGIN_LABELS[item.createdVia]}</p> : null}
                    </td>
                    {canWrite && (
                      <td className="ta-table__action">
                        {!isRevoking && (
                          <button
                            type="button"
                            className="button button-outline small ta-button-compact"
                            disabled={busy}
                            onClick={() => setRevoking({ item, reason: "" })}
                          >
                            Reincluir
                          </button>
                        )}
                      </td>
                    )}
                  </tr>,
                  isRevoking && (
                    <tr key={`${item.id}-revoke`} className="ta-table__row--form">
                      <td colSpan={columns}>
                        <form className="ta-form ta-revoke" onSubmit={revoke} aria-label="Reincluir título">
                          <p className="ta-panel__title">{`Reincluir o título ${item.titleNumber ?? item.billId}?`}</p>
                          <p className="ta-muted">O título volta para a pagar e pago em todas as obras depois da atualização do financeiro.</p>
                          <div className="ta-field ta-field--wide">
                            <label className="ta-field__label" htmlFor={`annulments-revoke-reason-${variant}`}>Motivo da reinclusão</label>
                            <textarea
                              id={`annulments-revoke-reason-${variant}`}
                              className="ta-input ta-input--area"
                              rows={2}
                              maxLength={REASON_MAX}
                              value={revoking.reason}
                              onChange={(event) => setRevoking((current) => ({ ...current, reason: event.target.value }))}
                            />
                            <p className="ta-field__hint">{`Entre ${REASON_MIN} e ${REASON_MAX} caracteres.`}</p>
                          </div>
                          <div className="ta-actions">
                            <button type="submit" className="button button-accent small" disabled={busy || !validReason(revoking.reason)}>
                              Confirmar reinclusão e atualizar financeiro
                            </button>
                            <button type="button" className="button button-ghost small" disabled={busy} onClick={() => setRevoking(null)}>Cancelar</button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  ),
                ];
              })}
              {items.length === 0 && status !== "loading" && (
                <tr><td colSpan={columns} className="ta-table__empty">Nenhum título anulado.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {revoked.length > 0 && (
          <details className="ta-history">
            <summary className="ta-history__summary">{`Reincluídos (${revoked.length})`}</summary>
            <ul className="ta-history__list">
              {revoked.map((item) => (
                <li key={item.id} className="ta-cell-text">
                  {`Título ${item.titleNumber ?? item.billId}: anulado por ${personOf(item.createdBy)} em ${civil(item.createdAt)}, reincluído por ${personOf(item.revokedBy)} em ${civil(item.revokedAt)}. Motivo da anulação: ${item.reason ?? "—"}.${item.revokeReason ? ` Motivo da reinclusão: ${item.revokeReason}.` : ""}`}
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>
    </div>
  );
};

export default TitleAnnulmentsManager;
